import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const DoctorsPage = ({ onButtonClick, onBack, onOpenDoctorServices }) => {
  const { t } = useTranslation();
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorReviews, setDoctorReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const doctorCardBackground = "/backgrounds/background.png";

  // Функция склонения слова "отзыв" - вынесена наверх
  const getReviewsWord = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return 'отзыв';
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'отзыва';
    return 'отзывов';
  };

  // Функция для отображения звезд (только целые звезды)
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const emptyStars = 5 - fullStars;
    
    return (
      <div className="doctor-rating-stars" style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {Array(fullStars).fill().map((_, i) => <span key={`full-${i}`}>⭐</span>)}
        {Array(emptyStars).fill().map((_, i) => <span key={`empty-${i}`} style={{ opacity: 0.3 }}>⭐</span>)}
        <span style={{ marginLeft: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
          ({rating.toFixed(1)})
        </span>
      </div>
    );
  };

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const data = await api.getDoctors();
        setDoctors(data);
      } catch (err) {
        console.error('Ошибка загрузки врачей:', err);
        setError('Не удалось загрузить список врачей');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDoctors();
  }, []);

  const handleSelectDoctor = async (doctor) => {
    setSelectedDoctor(doctor);
    try {
      const reviews = await api.getReviews(doctor.id, null, 'approved');
      setDoctorReviews(reviews);
    } catch (err) {
      console.error('Ошибка загрузки отзывов:', err);
      setDoctorReviews([]);
    }
  };

  const handleBackToList = () => {
    setSelectedDoctor(null);
    setDoctorReviews([]);
  };

  const handleBookDoctor = (doctor) => {
    onOpenDoctorServices(doctor);
  };

  if (loading) {
    return (
      <div className="doctors-page">
        <header className="header">
          <div className="clinic-logo">
            <div className="logo-icon">👨‍⚕️</div>
            <h1>{t('doctorsPage.title')}</h1>
          </div>
        </header>
        <main className="main-content">
          <div className="loading-spinner" style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
            Загрузка врачей...
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="doctors-page">
        <header className="header">
          <div className="clinic-logo">
            <div className="logo-icon">👨‍⚕️</div>
            <h1>{t('doctorsPage.title')}</h1>
          </div>
        </header>
        <main className="main-content">
          <div className="error-message" style={{ textAlign: 'center', padding: '40px', color: '#e74c3c' }}>
            {error}
            <button 
              className="service-btn" 
              onClick={() => window.location.reload()}
              style={{ marginTop: '20px' }}
            >
              Попробовать снова
            </button>
          </div>
        </main>
        <div className="doctors-footer">
          <button className="service-btn back-btn" onClick={onBack}>
            <span className="btn-icon">←</span>
            <span className="btn-text">{t('doctorsPage.backToMain')}</span>
          </button>
        </div>
      </div>
    );
  }

  if (selectedDoctor) {
    return (
      <div className="doctors-page">
        <header className="header">
          <div className="clinic-logo">
            <div className="logo-icon">👨‍⚕️</div>
            <h1>{t('doctorsPage.title')}</h1>
          </div>
          
          <div className="user-greeting">
            <p>{t('doctorsPage.doctorInfo')}</p>
          </div>
        </header>

        <div className="doctors-navigation" style={{ marginBottom: '20px' }}>
          <button className="service-btn back-btn" onClick={handleBackToList}>
            <span className="btn-icon">←</span>
            <span className="btn-text">{t('doctorsPage.backToList')}</span>
          </button>
        </div>

        <main className="main-content">
          <div className="doctor-card-detailed" style={{
            backgroundImage: `url(${doctorCardBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}>
            <div className="doctor-card-content">
              <div className="doctor-photo-container">
                <div className="doctor-photo-frame">
                  <img 
                    src={selectedDoctor.image_url} 
                    alt={selectedDoctor.name}
                    className="doctor-photo-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = 
                        `<div class="doctor-photo-fallback">${selectedDoctor.specialty?.substring(0, 2) || '👨‍⚕️'}</div>`;
                    }}
                  />
                </div>
              </div>
              
              <div className="doctor-main-info">
                <h3 className="doctor-specialty-large">{selectedDoctor.specialty}</h3>
                <p className="doctor-name-large">{selectedDoctor.name}</p>
                
                {/* Рейтинг врача - данные из бэкенда */}
                {selectedDoctor.rating > 0 && (
                  <div className="doctor-rating" style={{ marginBottom: '15px' }}>
                    {renderStars(selectedDoctor.rating)}
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginLeft: '5px' }}>
                      ({selectedDoctor.reviews_count || 0} {getReviewsWord(selectedDoctor.reviews_count || 0)})
                    </span>
                  </div>
                )}
                
                <button 
                  className="book-doctor-btn"
                  onClick={() => handleBookDoctor(selectedDoctor)}
                >
                  <span className="book-icon">📅</span>
                  <span className="book-text">{t('doctorsPage.bookAppointment')}</span>
                </button>
              </div>

              <div className="doctor-description">
                <h4 className="description-title">{t('doctorsPage.aboutDoctor')}</h4>
                <p className="description-text">
                  {selectedDoctor.description || 'Информация о враче временно недоступна'}
                </p>
              </div>

              {/* Отзывы - показываем только если есть */}
              {doctorReviews.length > 0 && (
                <div className="doctor-reviews" style={{ marginTop: '20px' }}>
                  <h4 className="description-title" style={{ marginBottom: '15px' }}>📝 Отзывы пациентов</h4>
                  <div className="reviews-list">
                    {doctorReviews.map((review) => (
                      <div key={review.id} className="review-item" style={{
                        background: 'rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        padding: '12px',
                        marginBottom: '10px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600', color: 'white' }}>{review.first_name} {review.last_name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            {Array(review.rating).fill().map((_, i) => <span key={i}>⭐</span>)}
                          </div>
                        </div>
                        {review.comment && (
                          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', margin: '0', lineHeight: '1.4' }}>
                            {review.comment}
                          </p>
                        )}
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '8px', display: 'block' }}>
                          {new Date(review.created_at).toLocaleDateString('ru-RU')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <div className="doctors-footer">
          <button className="service-btn back-btn" onClick={onBack}>
            <span className="btn-icon">←</span>
            <span className="btn-text">{t('doctorsPage.backToMain')}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="doctors-page">
      <header className="header">
        <div className="clinic-logo">
          <div className="logo-icon">👨‍⚕️</div>
          <h1>{t('doctorsPage.title')}</h1>
        </div>
        
        <div className="user-greeting">
          <p>{t('doctorsPage.subtitle')}</p>
        </div>
      </header>

      <main className="main-content">
        <div className="welcome-section">
          <h2>{t('doctorsPage.ourSpecialists')}</h2>
          <p className="subtitle">{t('doctorsPage.chooseDoctor')}</p>
        </div>

        <div className="doctors-list-simple">
          {doctors.map((doctor) => (
            <div 
              key={doctor.id} 
              className="doctor-card-simple"
              onClick={() => handleSelectDoctor(doctor)}
            >
              <div className="doctor-card-simple-content">
                <div className="doctor-photo-simple">
                  <img 
                    src={doctor.image_url} 
                    alt={doctor.name}
                    className="doctor-photo-img-simple"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = 
                        `<div class="doctor-photo-fallback-simple">${doctor.specialty?.substring(0, 1) || '👨‍⚕️'}</div>`;
                    }}
                  />
                </div>
                <div className="doctor-info-simple">
                  <h3 className="doctor-specialty-simple">{doctor.specialty}</h3>
                  <p className="doctor-name-simple">{doctor.name}</p>
                  {doctor.rating > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        {Array(Math.floor(doctor.rating)).fill().map((_, i) => <span key={i}>⭐</span>)}
                        {doctor.rating % 1 >= 0.5 && <span style={{ opacity: 0.5 }}>⭐</span>}
                      </div>
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                        ({doctor.reviews_count} {getReviewsWord(doctor.reviews_count)})
                      </span>
                    </div>
                  )}
                  {doctor.description && (
                    <p className="doctor-description-short" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '5px' }}>
                      {doctor.description.length > 60 ? doctor.description.substring(0, 60) + '...' : doctor.description}
                    </p>
                  )}
                </div>
                <div className="doctor-arrow">→</div>
              </div>
            </div>
          ))}
        </div>

        <div className="doctors-footer">
          <button className="service-btn back-btn" onClick={onBack}>
            <span className="btn-icon">←</span>
            <span className="btn-text">{t('doctorsPage.backToMain')}</span>
          </button>
        </div>
      </main>
    </div>
  );
};

export default DoctorsPage;